import { Menu, MenuDivider, MenuItem } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import * as React from 'react';
import { usePortalConnections, usePortalUser } from 'vet-bones/utils/portal';

import { TEST_IDS, getConnectionMenuItemTestId } from 'src/constants/testIds';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import { coercePortalConnection } from 'src/store/connections/types';
import { icons } from 'src/templates/icons';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type ConnectionMenuProps = {
  children: React.ReactNode;
  /**
   * whether we should connect immediately on connection select,
   * or just set connection on our state
   */
  connect?: boolean;
  /** don't allow popover to show */
  disabled?: boolean;
};

export const ConnectionMenu: React.VFC<ConnectionMenuProps> = ({
  children,
  connect = true,
  disabled = false,
}) => {
  const dispatch = useReduxDispatch();
  const currentConnection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const portalUser = usePortalUser();
  const portalConnections = usePortalConnections();

  const portalAccountName = portalUser.data?.email || portalUser.data?.username;

  const menu = (
    <Menu data-testid={TEST_IDS.connection.menu}>
      <MenuDivider title={portalAccountName} />

      {portalConnections.data?.map((connection, index) => (
        <MenuItem
          key={connection.id}
          active={isConnected && connection.id === currentConnection?.id}
          data-testid={getConnectionMenuItemTestId(connection.id, index)}
          icon={icons.connection}
          onClick={() => {
            dispatch(
              setupPortalConnection(coercePortalConnection(connection), {
                connect,
              })
            );
          }}
          text={connection.name}
        />
      ))}
    </Menu>
  );

  return (
    <Popover2
      // since our navbar is dark theme, don't inherit that
      content={menu}
      disabled={disabled}
      inheritDarkTheme={false}
    >
      {children}
    </Popover2>
  );
};
